function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function toggleProductMediaPlayback(event) {
  const toggle = event.target.closest('.product-media-pause-toggle');
  if (!toggle) return;

  const container = toggle.closest('.media');
  if (!container) return;

  const isPaused = toggle.classList.contains('product-media-pause-toggle--paused');
  const video = container.querySelector('video');
  const iframe = container.querySelector('iframe');

  if (video) {
    isPaused ? video.play() : video.pause();
  } else if (iframe) {
    const message = iframe.classList.contains('js-youtube')
      ? '{"event":"command","func":"' + (isPaused ? 'playVideo' : 'pauseVideo') + '","args":""}'
      : JSON.stringify({ method: isPaused ? 'play' : 'pause' });
    iframe.contentWindow.postMessage(message, '*');
  }

  toggle.classList.toggle('product-media-pause-toggle--paused', !isPaused);
  toggle.setAttribute('aria-label', isPaused ? window.accessibilityStrings.pauseVideo : window.accessibilityStrings.playVideo);
}
document.addEventListener('click', toggleProductMediaPlayback);

function removeAutoplayingMediaFromTabOrder() {
  document.querySelectorAll('.product-media-pause-toggle').forEach((toggle) => {
    const container = toggle.closest('.media');
    if (!container) return;
    const mediaElement = container.querySelector('video, iframe');
    if (mediaElement) mediaElement.tabIndex = -1;
  });
}
document.addEventListener('DOMContentLoaded', removeAutoplayingMediaFromTabOrder);

// Looping videos marked .js-lazy-autoplay render with preload="none" and no autoplay,
// so nothing downloads until they near the viewport. They play while on screen, pause
// off screen, stay paused once the shopper pauses them, and start paused for reduced motion.
function initLazyAutoplayVideos() {
  const videos = document.querySelectorAll('video.js-lazy-autoplay');
  if (!videos.length) return;

  const getToggle = (video) => video.closest('.media')?.querySelector('.product-media-pause-toggle');

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    videos.forEach((video) => {
      const toggle = getToggle(video);
      if (!toggle) return;
      toggle.classList.add('product-media-pause-toggle--paused');
      toggle.setAttribute('aria-label', window.accessibilityStrings.playVideo);
    });
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(({ isIntersecting, target: video }) => {
        if (!isIntersecting) return video.pause();
        if (getToggle(video)?.classList.contains('product-media-pause-toggle--paused')) return;
        video.play().catch(() => {});
      });
    },
    { rootMargin: '200px 0px' }
  );
  videos.forEach((video) => observer.observe(video));
}
document.addEventListener('DOMContentLoaded', initLazyAutoplayVideos);

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

// Set --header-bottom-position-desktop for the desktop nav overlay.
// The Levain site uses .nav-menu.js-hover (pure CSS hover) rather than
// Dawn's header-menu custom element, so details-disclosure.js's HeaderMenu
// never instantiates and never sets this variable. We set it here instead.
//
// The overlay is position:fixed with top:var(--header-bottom-position-desktop),
// so this value must equal the header's *live* bottom edge in the viewport. The
// header is not sticky (sticky_header_type: none) and sits below two sibling
// sections (announcement bar + ticker), so its viewport-space bottom changes as
// the page scrolls. Recomputing only on load + a ResizeObserver left the overlay
// pinned to a stale value after a scroll-restored load: the header's box size
// never changes on scroll-to-top, so the observer never re-fired, and the mask
// then peeked out above the header. Recompute live — on scroll, on resize, and
// (synchronously) the moment the pointer or focus enters the nav, right before
// the CSS :hover overlay can appear.
(function initNavOverlayPosition() {
  const root = document.documentElement;
  let ticking = false;
  let lastValue = null;

  function measure() {
    const header = document.querySelector('.header-wrapper');
    if (!header) return null;
    // The overlay only renders at >=990px (the inline menu is display:none
    // below that), so skip the layout read otherwise.
    if (window.innerWidth < 990) return null;
    const bottom = header.getBoundingClientRect().bottom;
    if (bottom > 0) return Math.round(bottom);
    // Header scrolled above the viewport (e.g. a scroll-restored load): the
    // overlay isn't visible in this state, but fall back to the full header
    // group's height (announcement bar + ticker + header) so the value stays
    // sane until the next scroll/hover recomputes it live. offsetHeight alone
    // would exclude those sibling bars and be too small.
    const group = document.querySelector('.shopify-section-group-header-group');
    return Math.round(group ? group.getBoundingClientRect().height : header.offsetHeight);
  }

  function setPosition() {
    const value = measure();
    // Only write when the value actually changes, so scrolling in the page body
    // (where the header is off-screen) doesn't invalidate style every frame.
    if (value === null || value === lastValue) return;
    lastValue = value;
    root.style.setProperty('--header-bottom-position-desktop', `${value}px`);
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      setPosition();
    });
  }

  function attachNavRefresh() {
    const nav = document.querySelector('.header__inline-menu');
    // Idempotent: a re-rendered nav (Theme Editor) is a fresh node without the
    // flag and re-binds; an already-bound node is skipped.
    if (!nav || nav.dataset.navOverlayBound) return;
    nav.dataset.navOverlayBound = 'true';
    // Synchronous refresh just before the overlay can show, so the first
    // hover/focus after any layout shift is correct with no rAF lag that could
    // flash the mask at a stale position during its 0.3s fade-in.
    nav.addEventListener('pointerenter', setPosition);
    nav.addEventListener('focusin', setPosition);
    if (window.ResizeObserver) {
      const header = document.querySelector('.header-wrapper');
      if (header) new ResizeObserver(setPosition).observe(header);
    }
  }

  function init() {
    setPosition();
    attachNavRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Live correction as the header's viewport position changes.
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  // Re-bind element listeners if the Theme Editor re-renders the header.
  document.addEventListener('shopify:section:load', attachNavRefresh);
})();

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    
    // Calculate offset using the full width of the item including margins
    const firstItem = this.sliderItemsToShow[0];
    const computedStyle = window.getComputedStyle(firstItem);
    const marginLeft = parseFloat(computedStyle.marginLeft);
    const marginRight = parseFloat(computedStyle.marginRight);
    
    this.sliderItemOffset = firstItem.offsetWidth + marginLeft + marginRight;
    
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    if (!this.slider || !this.nextButton) return;
    const previousPage = this.currentPage;
    // Calculate current page based on scroll position divided by slide offset
    
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    // Check if we've reached the end of the slider
    const lastSlideScrollPosition = this.slider.scrollWidth - this.slider.clientWidth;
    if (Math.abs(this.slider.scrollLeft - lastSlideScrollPosition) < 1) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    const direction = event.currentTarget.name === 'next' ? 1 : -1;
    
    // Calculate the next position ensuring proper alignment
    const currentScrollPosition = this.slider.scrollLeft;
    const targetPosition = currentScrollPosition + (direction * step * this.sliderItemOffset);
    
    // Round to the nearest slide position to prevent partial slides
    this.slideScrollPosition = Math.round(targetPosition / this.sliderItemOffset) * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
      behavior: 'smooth' // Add smooth scrolling
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider || !this.sliderItems?.length) return;

    const itemsCount = this.sliderItems.length;
    if (itemsCount === 0) return;

    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    // Guard clause to prevent accessing classList on undefined elements
    if (!nextSlide || !currentSlide) return;

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.button--primary').forEach(button => {
    button.addEventListener('click', () => {
      button.classList.add('no-hover');
      setTimeout(() => {
        button.classList.remove('no-hover');
      }, 3000);
    });
  });
});

// Product Gallery Navigation
document.addEventListener('DOMContentLoaded', () => {
  const prevButton = document.getElementById('prev-gallery-img');
  const nextButton = document.getElementById('next-gallery-img');
  
  if (prevButton && nextButton) {
    prevButton.addEventListener('click', () => navigateGallery('previous'));
    nextButton.addEventListener('click', () => navigateGallery('next'));
    
    // Listen for thumbnail clicks and update button states
    const thumbnails = document.querySelectorAll('.thumbnail-list__item button');
    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        // Use setTimeout to allow the default thumbnail click handler to complete first
        setTimeout(() => updateGalleryButtonStates(), 10);
      });
    });
    
    // Initial button state setup
    updateGalleryButtonStates();
  }
});

function updateGalleryButtonStates() {
  const slides = document.querySelectorAll('.product__media-item');
  if (!slides.length) return;

  const activeSlide = document.querySelector('.product__media-item.is-active');
  if (!activeSlide) return;

  const currentIndex = Array.from(slides).indexOf(activeSlide);
  const prevButton = document.getElementById('prev-gallery-img');
  const nextButton = document.getElementById('next-gallery-img');

  // Update previous button state
  if (currentIndex === 0) {
    prevButton?.classList.add('disabled');
  } else {
    prevButton?.classList.remove('disabled');
  }

  // Update next button state
  if (currentIndex === slides.length - 1) {
    nextButton?.classList.add('disabled');
  } else {
    nextButton?.classList.remove('disabled');
  }
}

function navigateGallery(direction) {
  const slides = document.querySelectorAll('.product__media-item');
  if (!slides.length) return;

  const activeSlide = document.querySelector('.product__media-item.is-active');
  if (!activeSlide) return;

  const currentIndex = Array.from(slides).indexOf(activeSlide);
  const prevButton = document.getElementById('prev-gallery-img');
  const nextButton = document.getElementById('next-gallery-img');

  let nextIndex;
  if (direction === 'next') {
    nextIndex = currentIndex + 1;
  } else {
    nextIndex = currentIndex - 1;
  }

  // Remove active class from current slide
  activeSlide.classList.remove('is-active');
  
  // Add active class to next slide
  slides[nextIndex].classList.add('is-active');

  // Handle button states after updating slide
  updateGalleryButtonStates();

  // Update thumbnail selection
  const thumbnails = document.querySelectorAll('.thumbnail-list__item button');
  if (thumbnails.length) {
    // Remove current state from previous thumbnail
    const currentThumbnail = document.querySelector('.thumbnail[aria-current="true"]');
    if (currentThumbnail) {
      currentThumbnail.removeAttribute('aria-current');
    }

    // Add current state to new thumbnail
    thumbnails[nextIndex].setAttribute('aria-current', 'true');
  }
}

// Product Details Accordion Functionality (WCAG compliant)
document.addEventListener('DOMContentLoaded', function() {
  const accordionTitles = document.querySelectorAll('.accordion__title');
  
  const toggleAccordion = function(e) {
    const accordion = this.parentNode;
    const isExpanded = accordion.classList.contains('active');
    
    // Toggle active state
    accordion.classList.toggle('active');
    
    // Update aria-expanded attribute for accessibility
    this.setAttribute('aria-expanded', !isExpanded);
  };

  accordionTitles.forEach(function(title) {
    title.addEventListener('click', toggleAccordion, false);
    
    // Keyboard support - Enter and Space (for buttons this is automatic, but ensuring compatibility)
    title.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleAccordion.call(this, e);
      }
    });
  });
});

/**
 * Tin GWP Helper Function
 * Checks if a variant qualifies for tin upgrade and returns the corresponding tin variant ID
 * @param {string} originalSku - The SKU of the original cookie variant
 * @returns {string|null} - The tin variant ID if found, null otherwise
 */
function getTinUpgradeVariantId(originalSku) {
  // Check if tin GWP is enabled and configured
  if (!window.TIN_GWP_ENABLED || !window.TIN_GWP_PRODUCT) {
    return null;
  }
  
  // Check if the SKU exists
  if (!originalSku) {
    return null;
  }
  
  // Extract SKU prefix (e.g., "GB04" from "GB04-CHOC")
  const skuPrefix = originalSku.split('-')[0];
  
  // Check if this SKU prefix qualifies for tin upgrade
  if (!window.TIN_QUALIFYING_SKU_PREFIXES || !window.TIN_QUALIFYING_SKU_PREFIXES.includes(skuPrefix)) {
    return null;
  }
  
  // Extract the SKU suffix to match with tin variants (e.g., "CHOC" from "GB04-CHOC")
  const skuSuffix = originalSku.split('-').slice(1).join('-');

  // Pack size from the prefix digits (e.g., "12" from "GB12"), used to pick the
  // matching size when a tin product carries both 8 and 12 pack variants
  // (e.g. ET08-ASRT / ET12-ASRT).
  const packSizeMatch = skuPrefix.match(/(\d+)$/);
  const packSize = packSizeMatch ? parseInt(packSizeMatch[1], 10) : null;

  const findTinVariant = (product) => {
    if (!product || !Array.isArray(product.variants)) return null;
    const flavorMatches = product.variants.filter(variant => variant.sku && variant.sku.endsWith(skuSuffix));
    if (!flavorMatches.length) return null;

    // Prefer an exact flavor match so "ASRT" doesn't pick "MFASRT"/"PBASRT"
    const exactFlavor = flavorMatches.filter(variant => variant.sku.split('-').slice(1).join('-') === skuSuffix);
    const candidates = exactFlavor.length ? exactFlavor : flavorMatches;

    if (packSize !== null) {
      const sameSize = candidates.find(variant => {
        const tinSizeMatch = variant.sku.split('-')[0].match(/(\d+)$/);
        return tinSizeMatch && parseInt(tinSizeMatch[1], 10) === packSize;
      });
      if (sameSize) return sameSize;
    }

    // Tin product has only one size (e.g. 8pk-only heritage tin) — keep prior behavior
    return candidates[0];
  };

  // Fall back to the single-flavor tin product when the primary tin has no matching variant
  const tinVariant = findTinVariant(window.TIN_GWP_PRODUCT) || findTinVariant(window.TIN_GWP_PRODUCT_FALLBACK);

  return tinVariant ? tinVariant.id : null;
}
window.getTinUpgradeVariantId = getTinUpgradeVariantId;

/**
 * Returns true if the given variant ID belongs to the Tin GWP product, its
 * single-flavor fallback, or the BYO tin — i.e. a tin added as part of the GWP promo.
 * @param {number|string} variantId
 * @returns {boolean}
 */
function isTinGWPVariant(variantId) {
  if (variantId == null) return false;
  const id = String(variantId);
  const inProduct = (product) =>
    !!product && Array.isArray(product.variants) &&
    product.variants.some((v) => String(v.id) === id);
  if (inProduct(window.TIN_GWP_PRODUCT)) return true;
  if (inProduct(window.TIN_GWP_PRODUCT_FALLBACK)) return true;
  if (window.TIN_GWP_BYO_VARIANT_ID && String(window.TIN_GWP_BYO_VARIANT_ID) === id) return true;
  return false;
}
window.isTinGWPVariant = isTinGWPVariant;

/**
 * Returns true if the given variant is a Tin GWP tin the customer is adding directly
 * (not via the cookie -> tin swap) whose pack size is one the promo targets, e.g. an
 * ET12 tin while the qualifying cookie prefixes are "GB12". Used to auto-apply the
 * promo code on direct tin adds.
 * @param {number|string} variantId
 * @returns {boolean}
 */
function isQualifyingTinGWPVariant(variantId) {
  if (!window.TIN_GWP_ENABLED || variantId == null) return false;
  const id = String(variantId);
  const tinVariant = [window.TIN_GWP_PRODUCT, window.TIN_GWP_PRODUCT_FALLBACK]
    .filter((product) => product && Array.isArray(product.variants))
    .flatMap((product) => product.variants)
    .find((v) => String(v.id) === id);
  if (!tinVariant) return false;

  const packSizeOf = (prefix) => {
    const match = String(prefix || '').match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  };
  const tinSize = packSizeOf((tinVariant.sku || '').split('-')[0]);
  // No size in the tin SKU -- nothing to mismatch on, so the tin qualifies
  if (tinSize === null) return true;

  const qualifyingSizes = (window.TIN_QUALIFYING_SKU_PREFIXES || []).map(packSizeOf);
  return qualifyingSizes.includes(tinSize);
}
window.isQualifyingTinGWPVariant = isQualifyingTinGWPVariant;

/**
 * Returns true if any of the given cart line items is a Tin GWP variant.
 * @param {Array} items - cart line items (each with id and/or variant_id)
 * @returns {boolean}
 */
function cartHasTinGWPProduct(items) {
  if (!Array.isArray(items)) return false;
  return items.some((item) =>
    isTinGWPVariant(item && (item.variant_id != null ? item.variant_id : item.id))
  );
}
window.cartHasTinGWPProduct = cartHasTinGWPProduct;

/**
 * Applies the Tin GWP promo discount CODE to the cart so the swapped-in tin shows
 * as discounted in the live cart drawer and carries through to checkout.
 *
 * We use a discount code instead of a Shopify automatic discount because two
 * automatic discounts (the tin discount + the free-shipping discount) do not
 * reliably stack. Hitting the storefront /discount/<code> link sets the discount
 * cookie; any cart section rendered afterwards reflects the discount — so callers
 * should await this BEFORE the cart/add (or section) request that follows.
 *
 * Idempotent: the /discount link is harmless to hit more than once, and an
 * in-memory flag avoids redundant requests within a page view. The configured
 * code MUST be restricted to the tin product(s) in the Shopify admin (and set to
 * combine with shipping discounts) so it only ever discounts the tin.
 *
 * @returns {Promise<void>}
 */
async function applyTinGWPDiscountCode() {
  const code = window.TIN_GWP_DISCOUNT_CODE;
  if (!code || window.__tinGWPDiscountApplied) return;
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    await fetch(`${root}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      redirect: 'manual',
      credentials: 'same-origin',
    });
    window.__tinGWPDiscountApplied = true;
  } catch (e) {
    console.error('[Tin GWP] Failed to apply discount code:', e);
  }
}
window.applyTinGWPDiscountCode = applyTinGWPDiscountCode;

/**
 * Applies the free-shipping A/B test discount code to the cart, for whichever
 * theme/arm has one configured. window.FREE_SHIPPING_AB_DISCOUNT_CODE is only ever
 * set (in theme.liquid) when settings.enable_free_shipping_bar is on, so a control
 * arm with the toggle off never has a code to apply here.
 *
 * Mirrors applyTinGWPDiscountCode(): hits the storefront /discount/<code> link to
 * set the discount cookie. The code's own minimum-subtotal rule (configured in
 * Checkout Blocks) decides whether it actually discounts anything -- applying it
 * unconditionally on page load doesn't force a discount for carts that don't
 * qualify yet.
 *
 * Idempotent: safe to call more than once per page view.
 *
 * @returns {Promise<void>}
 */
async function applyFreeShippingABDiscountCode() {
  const code = window.FREE_SHIPPING_AB_DISCOUNT_CODE;
  if (!code || window.__freeShippingABDiscountApplied) return;
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    await fetch(`${root}discount/${encodeURIComponent(code)}`, {
      method: 'GET',
      redirect: 'manual',
      credentials: 'same-origin',
    });
    window.__freeShippingABDiscountApplied = true;
  } catch (e) {
    console.error('[Free Shipping A/B] Failed to apply discount code:', e);
  }
}
window.applyFreeShippingABDiscountCode = applyFreeShippingABDiscountCode;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyFreeShippingABDiscountCode, { once: true });
} else {
  applyFreeShippingABDiscountCode();
}

/**
 * Sets a cart attribute recording which arm of the free-shipping A/B test this
 * published theme is ("test" or "control", from window.FREE_SHIPPING_AB_TEST_ARM,
 * always set in theme.liquid regardless of arm). Cart attributes carry straight
 * through to the order as a note attribute, so orders can be segmented by arm --
 * e.g. for AOV comparison -- directly from Shopify order data (CSV export or
 * Admin API), with no other tracking setup required.
 *
 * @returns {Promise<void>}
 */
async function tagCartWithFreeShippingABTestArm() {
  const arm = window.FREE_SHIPPING_AB_TEST_ARM;
  if (!arm) return;
  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: { ab_test_arm: arm } }),
    });
  } catch (e) {
    console.error('[Free Shipping A/B] Failed to tag cart with test arm:', e);
  }
}
window.tagCartWithFreeShippingABTestArm = tagCartWithFreeShippingABTestArm;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tagCartWithFreeShippingABTestArm, { once: true });
} else {
  tagCartWithFreeShippingABTestArm();
}

// Product card hover videos (card-product-media.liquid, hover_play) ship with preload="none" and
// no autoplay, so a grid of cards downloads nothing until one is hovered or focused. Delegated so
// cards rendered later (filtering, quick add) work too.
function toggleCardHoverVideo(event, play) {
  const card = event.target.closest?.('.card-wrapper');
  if (!card || card.contains(event.relatedTarget)) return;
  const video = card.querySelector('video.js-card-hover-video');
  if (!video) return;
  if (!play) {
    video.pause();
    video.currentTime = 0;
  } else if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    video.play().catch(() => {});
  }
}
document.addEventListener('mouseover', (event) => toggleCardHoverVideo(event, true));
document.addEventListener('mouseout', (event) => toggleCardHoverVideo(event, false));
document.addEventListener('focusin', (event) => toggleCardHoverVideo(event, true));
document.addEventListener('focusout', (event) => toggleCardHoverVideo(event, false));
